import type { GqlStatusObject, Notification, NotificationPosition, ResultSummary } from 'neo4j-driver';

import { formatDescriptionFromGqlStatusDescription, formatTitleFromGqlStatusDescription } from './gql-formatters';

export type FormattedNeo4jNotification = {
  title?: string;
  description: string;
  position?: NotificationPosition;
  code?: string | null;
};

export type FormattedNeo4jNotifications = {
  warningNotifications: FormattedNeo4jNotification[];
  infoNotifications: FormattedNeo4jNotification[];
};

const mapGqlStatusObjectsToFormattedNotifications = (
  statusObjects: Omit<GqlStatusObject, 'diagnosticRecordAsJsonString'>[],
): FormattedNeo4jNotification[] => {
  return statusObjects.map((statusObject) => {
    const { gqlStatus, statusDescription } = statusObject;
    return {
      title: formatTitleFromGqlStatusDescription(gqlStatus, statusDescription),
      description: formatDescriptionFromGqlStatusDescription(statusDescription),
      position: statusObject.position,
    };
  });
};

const mapNotificationsToFormattedNotifications = (notifications: Notification[]): FormattedNeo4jNotification[] => {
  return notifications.map((notification) => ({
    title: notification.title,
    description: notification.description,
    position: notification.position,
    code: notification.code,
  }));
};

const formatResultSummaryGqlStatusObjects = (resultSummary: Partial<ResultSummary>): FormattedNeo4jNotifications => {
  const warnings = resultSummary.gqlStatusObjects?.filter((n) => n.severity === 'WARNING') ?? [];
  const infos = resultSummary.gqlStatusObjects?.filter((n) => n.severity === 'INFORMATION') ?? [];
  return {
    warningNotifications: mapGqlStatusObjectsToFormattedNotifications(warnings),
    infoNotifications: mapGqlStatusObjectsToFormattedNotifications(infos),
  };
};

const formatResultSummaryNotifications = (resultSummary: Partial<ResultSummary>): FormattedNeo4jNotifications => {
  const warnings = resultSummary.notifications?.filter((n) => n.severity === 'WARNING') ?? [];
  const infos = resultSummary.notifications?.filter((n) => n.severity === 'INFORMATION') ?? [];
  return {
    warningNotifications: mapNotificationsToFormattedNotifications(warnings),
    infoNotifications: mapNotificationsToFormattedNotifications(infos),
  };
};

export const resultSummaryHasNotifications = (resultSummary: Partial<ResultSummary>): boolean => {
  const severities = ['WARNING', 'INFORMATION'];
  return (
    (resultSummary.gqlStatusObjects?.some((n) => severities.includes(n.severity)) ?? false) ||
    (resultSummary.notifications?.some((n) => severities.includes(n.severity)) ?? false)
  );
};

export const formatResultSummary = (
  resultSummary: Partial<ResultSummary>,
  showGqlErrorsAndNotifications: boolean,
): FormattedNeo4jNotifications => {
  return showGqlErrorsAndNotifications && resultSummary.gqlStatusObjects !== undefined
    ? formatResultSummaryGqlStatusObjects(resultSummary)
    : formatResultSummaryNotifications(resultSummary);
};
